// src/components/FileTransfer.js
//
// Peer-to-peer file transfer over a WebRTC data channel (files never touch a server).
// Signaling reuses the PeerJS identity channel from CallUI.js, so nobody types a peer id.
//
// <FileTransfer /> is mounted once (by Navbar.js). Send files from anywhere with:
//     sendFilesTo(userId, files, userRecord?)
// The receiver gets an Accept / Decline card, then a Download button for every file.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import pb from '../lib/pb';
import { useAuth } from '../contexts/AuthContext';
import { injectStyles } from '../shared/PostCard';
import { FriendAvatar, userName } from './FriendTile';
import { hub, rtcConfig, sendSignal, userIdOf } from './CallUI';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';

const CHUNK = 64 * 1024;                 // bytes per data-channel message
const BUF_HIGH = 4 * 1024 * 1024;        // pause sending above this much queued data
const MAX_BYTES = 500 * 1024 * 1024;     // received files are assembled in memory
const MAX_FILES = 50;

export function sendFilesTo(userId, files, user = null) {
  window.dispatchEvent(new CustomEvent('mf-send-files', { detail: { userId, files: Array.from(files || []), user } }));
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const rid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const safeName = (n) => String(n || 'file').replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').slice(0, 200) || 'file';

function fmtBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / 1024 / 1024).toFixed(1)} MB`;
  return `${(v / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function waitLow(dc) {
  return new Promise((resolve) => {
    if (dc.bufferedAmount <= dc.bufferedAmountLowThreshold) { resolve(); return; }
    dc.onbufferedamountlow = () => { dc.onbufferedamountlow = null; resolve(); };
  });
}

function download(f) {
  if (!f || !f.url) return;
  const a = document.createElement('a');
  a.href = f.url;
  a.download = f.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const pct = (it) => {
  const total = it.files.reduce((s, f) => s + f.size, 0) || 1;
  const got = it.files.reduce((s, f) => s + f.size * (f.progress || 0), 0);
  return Math.min(100, Math.round((got / total) * 100));
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */
const FT_CSS = `
.mft{position:fixed;z-index:9000;right:16px;bottom:calc(92px + env(safe-area-inset-bottom));width:min(380px,calc(100vw - 24px));max-height:70vh;overflow-y:auto;
  display:flex;flex-direction:column;gap:10px;scrollbar-width:none}
.mft::-webkit-scrollbar{display:none}
.mft-card{padding:14px;border-radius:18px;background:var(--mf-surface,#161b26);color:var(--mf-text,#f3f5f9);border:1px solid var(--mf-border-strong,rgba(255,255,255,.16));
  box-shadow:0 18px 44px rgba(0,0,0,.45);animation:mft-in .2s ease-out}
@keyframes mft-in{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.mft-head{display:flex;align-items:center;gap:10px}
.mft-title{flex:1;min-width:0}
.mft-title strong{display:block;font-size:14.5px;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.mft-title span{font-size:12.5px;color:var(--mf-muted,#aab2c2)}
.mft-x{width:30px;height:30px;border-radius:9px;border:0;background:transparent;color:var(--mf-muted,#aab2c2);display:grid;place-items:center;cursor:pointer;flex:none}
.mft-x:hover{background:var(--mf-surface-2,#1e2533);color:var(--mf-text,#f3f5f9)}
.mft-x svg{font-size:19px}
.mft-files{list-style:none;margin:10px 0 0;padding:0;display:flex;flex-direction:column;gap:8px}
.mft-file{display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:12px;background:var(--mf-surface-2,#1e2533);min-width:0}
.mft-ico{flex:none;width:36px;height:36px;border-radius:10px;display:grid;place-items:center;background:var(--mf-accent-soft,rgba(108,140,255,.16));color:var(--mf-accent,#6c8cff);overflow:hidden}
.mft-ico img{width:100%;height:100%;object-fit:cover}
.mft-ico svg{font-size:20px}
.mft-fi{flex:1;min-width:0}
.mft-fi b{display:block;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.mft-fi small{font-size:11.5px;color:var(--mf-faint,#7b8497)}
.mft-fi audio{display:block;width:100%;height:32px;margin-top:6px}
.mft-bar{height:6px;border-radius:999px;background:var(--mf-surface-3,#283042);overflow:hidden;margin-top:10px}
.mft-bar i{display:block;height:100%;border-radius:999px;background:var(--mf-grad,linear-gradient(135deg,#6c8cff,#b06cff));transition:width .15s}
.mft-acts{display:flex;gap:8px;justify-content:flex-end;margin-top:12px;flex-wrap:wrap}
.mft-btn{display:inline-flex;align-items:center;justify-content:center;gap:5px;height:34px;padding:0 14px;border-radius:10px;border:1px solid var(--mf-border-strong,rgba(255,255,255,.16));
  background:var(--mf-surface-3,#283042);color:var(--mf-text,#f3f5f9);font:inherit;font-size:13px;font-weight:700;cursor:pointer}
.mft-btn:hover{filter:brightness(1.12)}
.mft-btn svg{font-size:17px}
.mft-btn--primary{background:var(--mf-grad,linear-gradient(135deg,#6c8cff,#b06cff));border-color:transparent;color:#fff}
.mft-btn--danger{color:#ef4444}
.mft-ok{color:#16a34a;display:inline-flex;align-items:center;gap:4px;font-weight:700}
.mft-bad{color:#ef4444}
@media (prefers-reduced-motion:reduce){.mft-card{animation:none!important}}
`;
injectStyles('mf-ft-css', FT_CSS);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function FileTransfer() {
  const { user } = useAuth();
  const meRef = useRef(null);
  meRef.current = user;

  const [items, setItems] = useState([]);
  const sessions = useRef(new Map());   // sid -> { pc, dc, raw, meta, iceQ, gate, cur, userId, offer, cancelled }
  const lastP = useRef({});

  const patch = useCallback((sid, p) => {
    setItems((prev) => prev.map((i) => (i.sid === sid ? { ...i, ...(typeof p === 'function' ? p(i) : p) } : i)));
  }, []);

  const patchFile = useCallback((sid, fid, p, force) => {
    const now = Date.now();
    if (!force && p.progress != null && p.progress < 1 && now - (lastP.current[fid] || 0) < 120) return;
    lastP.current[fid] = now;
    setItems((prev) => prev.map((i) => (
      i.sid !== sid ? i : { ...i, files: i.files.map((f) => (f.id === fid ? { ...f, ...p } : f)) }
    )));
  }, []);

  const closeSession = useCallback((sid) => {
    const s = sessions.current.get(sid);
    if (!s) return;
    sessions.current.delete(sid);
    if (s.dc) { s.dc.onmessage = null; s.dc.onclose = null; try { s.dc.close(); } catch { /* ignore */ } }
    if (s.pc) {
      s.pc.onicecandidate = null;
      s.pc.onconnectionstatechange = null;
      try { s.pc.close(); } catch { /* ignore */ }
    }
  }, []);

  const fail = useCallback((sid, note) => {
    patch(sid, (i) => (i.status === 'done' ? {} : { status: 'failed', note }));
    closeSession(sid);
  }, [patch, closeSession]);

  const attachUser = useCallback((sid, uid) => {
    pb.collection('users').getOne(uid, { requestKey: null, $autoCancel: false })
      .then((u) => patch(sid, { user: u })).catch(() => {});
  }, [patch]);

  const makePc = useCallback((sid, uid) => {
    const s = sessions.current.get(sid);
    const pc = new RTCPeerConnection(rtcConfig());
    s.pc = pc;
    s.gate = { ready: false, q: [] };
    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      const msg = { t: 'ft-ice', sid, candidate: e.candidate.toJSON() };
      if (s.gate.ready) sendSignal(uid, msg).catch(() => {});
      else s.gate.q.push(msg);
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') fail(sid, 'Connection failed. Try again.');
    };
    return pc;
  }, [fail]);

  const openGate = useCallback((sid, uid) => {
    const s = sessions.current.get(sid);
    if (!s) return;
    s.gate.ready = true;
    const q = s.gate.q;
    s.gate.q = [];
    q.forEach((m) => sendSignal(uid, m).catch(() => {}));
  }, []);

  const flushIce = useCallback(async (s) => {
    const q = s.iceQ;
    s.iceQ = [];
    for (const c of q) { try { await s.pc.addIceCandidate(c); } catch { /* ignore */ } }
  }, []);

  /* ---------- sending ---------- */
  const pump = useCallback(async (sid) => {
    const s = sessions.current.get(sid);
    if (!s || !s.dc) return;
    const { dc } = s;
    patch(sid, { status: 'transferring' });
    try {
      for (let i = 0; i < s.raw.length; i += 1) {
        const f = s.raw[i];
        const m = s.meta[i];
        dc.send(JSON.stringify({ k: 'start', id: m.id, name: m.name, size: m.size, type: m.type }));
        let off = 0;
        while (off < f.size) {
          if (s.cancelled) return;
          if (dc.readyState !== 'open') throw new Error('closed');
          if (dc.bufferedAmount > BUF_HIGH) await waitLow(dc);
          const buf = await f.slice(off, off + CHUNK).arrayBuffer();
          dc.send(buf);
          off += buf.byteLength;
          patchFile(sid, m.id, { progress: off / f.size, status: 'sending' });
        }
        dc.send(JSON.stringify({ k: 'end', id: m.id }));
        patchFile(sid, m.id, { progress: 1, status: 'sent' }, true);
      }
      dc.send(JSON.stringify({ k: 'done' }));
      // the receiver answers with an "ack"; give up waiting after a while
      setTimeout(() => {
        if (sessions.current.has(sid)) { patch(sid, (it) => (it.status === 'done' ? {} : { status: 'done' })); closeSession(sid); }
      }, 30000);
    } catch (err) {
      if (!s.cancelled) fail(sid, 'The transfer was interrupted.');
    }
  }, [closeSession, fail, patch, patchFile]);

  const send = useCallback(async (userId, fileList, userRec) => {
    const me = meRef.current;
    if (!me || !userId) return;
    const all = Array.from(fileList || []).filter((f) => f && typeof f.size === 'number');
    if (!all.length) return;
    const files = all.filter((f) => f.size > 0 && f.size <= MAX_BYTES).slice(0, MAX_FILES);
    const sid = rid();

    if (!files.length) {
      setItems((prev) => [...prev, {
        sid, dir: 'out', userId, user: userRec || { id: userId }, status: 'failed',
        note: `Files must be between 1 byte and ${fmtBytes(MAX_BYTES)}.`, files: [],
      }]);
      return;
    }

    const meta = files.map((f, i) => ({
      id: `${sid}-${i}`,
      name: safeName(f.name),
      size: f.size,
      type: f.type || 'application/octet-stream',
    }));

    setItems((prev) => [...prev, {
      sid, dir: 'out', userId, user: userRec || { id: userId }, status: 'offering', note: '',
      files: meta.map((m) => ({ ...m, progress: 0, status: 'queued' })),
    }]);
    if (!userRec) attachUser(sid, userId);
    if (userRec && userRec.peerId) hub.hint(userId, userRec.peerId);

    try {
      sessions.current.set(sid, { dir: 'out', userId, raw: files, meta, iceQ: [], cancelled: false });
      const pc = makePc(sid, userId);
      const dc = pc.createDataChannel('files', { ordered: true });
      dc.binaryType = 'arraybuffer';
      dc.bufferedAmountLowThreshold = 1024 * 1024;
      const s = sessions.current.get(sid);
      s.dc = dc;
      dc.onopen = () => pump(sid);
      dc.onclose = () => {
        const cur = sessions.current.get(sid);
        if (cur && !cur.cancelled) patch(sid, (it) => (['done', 'failed', 'declined', 'cancelled'].includes(it.status) ? {} : { status: 'failed', note: 'The connection closed.' }));
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await sendSignal(userId, { t: 'ft-offer', sid, files: meta, sdp: pc.localDescription.sdp, from: me.id, name: userName(me) });
      openGate(sid, userId);
      patch(sid, { status: 'waiting' });
    } catch (err) {
      const unreachable = err && (err.message === 'PEER_UNREACHABLE' || err.message === 'PEER_NOT_READY');
      fail(sid, unreachable
        ? `${userName(userRec)} is offline or unreachable. They need to be online to receive files.`
        : 'Could not start the transfer.');
    }
  }, [attachUser, fail, makePc, openGate, patch, pump]);

  /* ---------- receiving ---------- */
  const onChannelMessage = useCallback((sid, ev) => {
    const s = sessions.current.get(sid);
    if (!s) return;

    if (typeof ev.data === 'string') {
      let m = null;
      try { m = JSON.parse(ev.data); } catch { return; }
      if (m.k === 'start') {
        const meta = s.meta.find((x) => x.id === m.id);
        if (!meta) return;
        s.cur = { id: m.id, chunks: [], got: 0, size: meta.size, type: meta.type, name: meta.name };
        patch(sid, { status: 'transferring' });
        patchFile(sid, m.id, { status: 'receiving', progress: 0 }, true);
      } else if (m.k === 'end' && s.cur && s.cur.id === m.id) {
        const c = s.cur;
        s.cur = null;
        if (c.got !== c.size) { fail(sid, 'A file arrived incomplete.'); return; }
        const url = URL.createObjectURL(new Blob(c.chunks, { type: c.type }));
        patchFile(sid, c.id, { status: 'done', progress: 1, url }, true);
      } else if (m.k === 'done') {
        try { s.dc.send(JSON.stringify({ k: 'ack' })); } catch { /* ignore */ }
        patch(sid, { status: 'done' });
        setTimeout(() => closeSession(sid), 1500);
      }
      return;
    }

    if (s.cur) {
      s.cur.chunks.push(ev.data);
      s.cur.got += ev.data.byteLength;
      patchFile(sid, s.cur.id, { progress: Math.min(1, s.cur.got / s.cur.size) });
    }
  }, [closeSession, fail, patch, patchFile]);

  const accept = useCallback(async (sid) => {
    const s = sessions.current.get(sid);
    if (!s || s.dir !== 'in' || s.pc) return;
    patch(sid, { status: 'connecting' });
    try {
      const pc = makePc(sid, s.userId);
      pc.ondatachannel = (e) => {
        s.dc = e.channel;
        s.dc.binaryType = 'arraybuffer';
        s.dc.onmessage = (ev) => onChannelMessage(sid, ev);
        s.dc.onclose = () => {
          patch(sid, (it) => (['done', 'failed', 'declined', 'cancelled'].includes(it.status) ? {} : { status: 'failed', note: 'The sender disconnected.' }));
        };
      };
      await pc.setRemoteDescription({ type: 'offer', sdp: s.offer });
      await flushIce(s);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await sendSignal(s.userId, { t: 'ft-answer', sid, sdp: pc.localDescription.sdp });
      openGate(sid, s.userId);
    } catch (err) {
      fail(sid, 'Could not connect to the sender.');
    }
  }, [fail, flushIce, makePc, onChannelMessage, openGate, patch]);

  const decline = useCallback((sid) => {
    const s = sessions.current.get(sid);
    if (s) sendSignal(s.userId, { t: 'ft-decline', sid }).catch(() => {});
    closeSession(sid);
    setItems((prev) => prev.filter((i) => i.sid !== sid));
  }, [closeSession]);

  const cancel = useCallback((sid) => {
    const s = sessions.current.get(sid);
    if (s) {
      s.cancelled = true;
      sendSignal(s.userId, { t: 'ft-cancel', sid }).catch(() => {});
    }
    patch(sid, { status: 'cancelled' });
    closeSession(sid);
  }, [closeSession, patch]);

  const dismiss = useCallback((sid) => {
    closeSession(sid);
    setItems((prev) => {
      const it = prev.find((i) => i.sid === sid);
      if (it) it.files.forEach((f) => { if (f.url) URL.revokeObjectURL(f.url); });
      return prev.filter((i) => i.sid !== sid);
    });
  }, [closeSession]);

  /* ---------- signaling ---------- */
  useEffect(() => {
    const onSignal = async (uid, msg) => {
      if (!msg || typeof msg.t !== 'string' || !msg.t.startsWith('ft-')) return;
      const s = sessions.current.get(msg.sid);

      try {
        switch (msg.t) {
          case 'ft-offer': {
            const meta = (Array.isArray(msg.files) ? msg.files : []).slice(0, MAX_FILES).map((f, i) => ({
              id: String(f.id || `${msg.sid}-${i}`),
              name: safeName(f.name),
              size: Math.max(0, Math.min(Number(f.size) || 0, MAX_BYTES)),
              type: String(f.type || 'application/octet-stream').slice(0, 100),
            }));
            if (!meta.length || sessions.current.has(msg.sid)) return;
            sessions.current.set(msg.sid, { dir: 'in', userId: uid, offer: msg.sdp, iceQ: [], meta, cur: null });
            setItems((prev) => [...prev, {
              sid: msg.sid, dir: 'in', userId: uid, user: { id: uid, name: msg.name }, status: 'offered', note: '',
              files: meta.map((m) => ({ ...m, progress: 0, status: 'offered' })),
            }]);
            attachUser(msg.sid, uid);
            break;
          }
          case 'ft-answer':
            if (s && s.pc && s.dir === 'out') {
              await s.pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp });
              await flushIce(s);
              patch(msg.sid, { status: 'connecting' });
            }
            break;
          case 'ft-ice':
            if (!s) return;
            if (s.pc && s.pc.remoteDescription) { try { await s.pc.addIceCandidate(msg.candidate); } catch { /* ignore */ } }
            else s.iceQ.push(msg.candidate);
            break;
          case 'ft-decline':
            if (s) { patch(msg.sid, { status: 'declined' }); closeSession(msg.sid); }
            break;
          case 'ft-cancel':
            if (s) { patch(msg.sid, (i) => (i.status === 'done' ? {} : { status: 'cancelled' })); closeSession(msg.sid); }
            break;
          case 'ft-ack':
            if (s && s.dir === 'out') { patch(msg.sid, { status: 'done' }); setTimeout(() => closeSession(msg.sid), 800); }
            break;
          default:
        }
      } catch (err) {
        console.error('File transfer signal error', err);
        if (s) fail(msg.sid, 'The transfer failed.');
      }
    };

    hub.listeners.add(onSignal);
    return () => { hub.listeners.delete(onSignal); };
  }, [attachUser, closeSession, fail, flushIce, patch]);

  // "mf-send-files" window event (used by the chat screen)
  useEffect(() => {
    const onSend = (e) => {
      const d = (e && e.detail) || {};
      send(d.userId || userIdOf(d.peerId), d.files, d.user || null);
    };
    window.addEventListener('mf-send-files', onSend);
    return () => window.removeEventListener('mf-send-files', onSend);
  }, [send]);

  // release everything on unmount
  useEffect(() => () => {
    Array.from(sessions.current.keys()).forEach((sid) => closeSession(sid));
  }, [closeSession]);

  /* ---------- UI ---------- */
  if (!items.length) return null;

  const body = (
    <div className="mft" aria-live="polite">
      {items.map((it) => {
        const name = userName(it.user);
        const n = it.files.length;
        const label = `${n} file${n === 1 ? '' : 's'}`;
        const live = ['transferring', 'connecting', 'waiting', 'offering'].includes(it.status);

        let title;
        let sub;
        if (it.dir === 'in') {
          title = it.status === 'offered' ? `${name} wants to send you ${label}` : `From ${name}`;
          sub = it.status === 'offered' ? 'Accept to start the transfer'
            : it.status === 'connecting' ? 'Connecting…'
            : it.status === 'transferring' ? `Receiving… ${pct(it)}%`
            : it.status === 'done' ? 'Received. Download your files below.'
            : it.note || (it.status === 'cancelled' ? 'Cancelled' : 'Failed');
        } else {
          title = `Sending ${label} to ${name}`;
          sub = it.status === 'offering' ? 'Connecting…'
            : it.status === 'waiting' ? `Waiting for ${name} to accept…`
            : it.status === 'connecting' ? 'Connecting…'
            : it.status === 'transferring' ? `Sending… ${pct(it)}%`
            : it.status === 'done' ? 'Sent'
            : it.status === 'declined' ? `${name} declined`
            : it.note || (it.status === 'cancelled' ? 'Cancelled' : 'Failed');
        }

        const doneAll = it.files.filter((f) => f.url);
        return (
          <section className="mft-card" key={it.sid}>
            <div className="mft-head">
              <FriendAvatar user={it.user} size={38} />
              <div className="mft-title">
                <strong>{title}</strong>
                <span className={['failed', 'declined', 'cancelled'].includes(it.status) ? 'mft-bad' : ''}>
                  {it.status === 'done' && it.dir === 'out'
                    ? <span className="mft-ok"><CheckRoundedIcon fontSize="inherit" /> Sent</span>
                    : sub}
                </span>
              </div>
              <button type="button" className="mft-x" aria-label="Dismiss" onClick={() => (live && it.dir === 'out' ? cancel(it.sid) : dismiss(it.sid))}>
                <CloseRoundedIcon />
              </button>
            </div>

            {it.files.length > 0 && (
              <ul className="mft-files">
                {it.files.map((f) => (
                  <li className="mft-file" key={f.id}>
                    <span className="mft-ico">
                      {f.url && f.type.startsWith('image/') ? <img src={f.url} alt="" /> : <InsertDriveFileRoundedIcon />}
                    </span>
                    <span className="mft-fi">
                      <b title={f.name}>{f.name}</b>
                      <small>{fmtBytes(f.size)}</small>
                      {f.url && f.type.startsWith('audio/') && <audio controls src={f.url} preload="metadata" />}
                    </span>
                    {f.url && (
                      <button type="button" className="mft-btn mft-btn--primary" onClick={() => download(f)} aria-label={`Download ${f.name}`}>
                        <DownloadRoundedIcon /> Save
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {it.status === 'transferring' && <div className="mft-bar"><i style={{ width: `${pct(it)}%` }} /></div>}

            <div className="mft-acts">
              {it.dir === 'in' && it.status === 'offered' && (
                <>
                  <button type="button" className="mft-btn" onClick={() => decline(it.sid)}>Decline</button>
                  <button type="button" className="mft-btn mft-btn--primary" onClick={() => accept(it.sid)}>Accept</button>
                </>
              )}
              {it.dir === 'in' && doneAll.length > 1 && (
                <button
                  type="button"
                  className="mft-btn"
                  onClick={() => doneAll.forEach((f, i) => setTimeout(() => download(f), i * 350))}
                >
                  <DownloadRoundedIcon /> Save all
                </button>
              )}
              {live && it.dir === 'out' && (
                <button type="button" className="mft-btn mft-btn--danger" onClick={() => cancel(it.sid)}>Cancel</button>
              )}
              {live && it.dir === 'in' && it.status !== 'offered' && (
                <button type="button" className="mft-btn mft-btn--danger" onClick={() => cancel(it.sid)}>Cancel</button>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );

  return createPortal(body, document.querySelector('.mf-feed') || document.body);
}